import { Connector } from '@web3-react/types';
import Web3 from 'web3';
import { prefix0X } from '../../utils/prefix0x';
import { contractAbi } from '../../contractAbi';
import { TransactionStatus } from '../../store/actions/depositFileActions';
import { CONTRACT_ADDRESS, PRICE_PER_VALIDATOR } from '../../utils/envVars';
import { DepositKeyInterface } from '../../store/reducers';

const TX_VALUE = `${PRICE_PER_VALIDATOR}000000000000000000`;

/**
 * Track in-flight transactions to prevent double deposits (GitLab #5)
 *
 * This Set stores pubkeys of transactions that are currently being processed.
 * A transaction is added when processing starts and removed when it completes
 * (success, failure, or rejection).
 */
const inFlightTransactions = new Set<string>();

/**
 * Get the current set of in-flight transaction pubkeys.
 * Exported for testing purposes.
 */
export const getInFlightTransactions = (): string[] => {
  return Array.from(inFlightTransactions);
};

/**
 * Check if a transaction is currently in-flight.
 */
export const isTransactionInFlight = (pubkey: string): boolean => {
  return inFlightTransactions.has(pubkey);
};

/**
 * Add a transaction to in-flight tracking.
 */
export const addTransactionInFlight = (pubkey: string): void => {
  inFlightTransactions.add(pubkey);
};

/**
 * Remove a transaction from in-flight tracking.
 */
export const removeTransactionInFlight = (pubkey: string): void => {
  inFlightTransactions.delete(pubkey);
};

/**
 * Clear all in-flight transactions.
 * Exported for testing purposes.
 */
export const clearInFlightTransactions = (): void => {
  inFlightTransactions.clear();
};

const isIgnorableError = (error: any) => {
  if (error.code === -32000) return true; // ALREADY_EXISTS: already known
  if (error.message.includes('Transaction was not mined within 50 blocks'))
    return true;
  return false;
};

const isUserRejectionError = (error: any) => {
  if (error.code === 4001) return true; // Metamask reject
  if (error.code === -32603) {
    // 0. Metamask deny signature
    if (
      error.message.includes(
        'MetaMask Tx Signature: User denied transaction signature.'
      )
    )
      return true;
    // 1. Ledger time-out or no-connect via Metamask
    if (error.message.includes('​Ledger device: U2F OTHER_ERROR')) return false; // false because the TX can be submitted after the Metamask timeout period
    // 2. Ledger reject via Metamask
    if (
      error.message.includes(
        'Ledger device: Condition of use not satisfied (denied by the user?)'
      )
    )
      return true;
    // 3. Trezor reject via Metamask
    if (error.message.includes('Action cancelled by user')) return true;
    // 4. Trezor popup closed via Metamask
    if (error.message.includes('Popup closed')) return true;
    // 5. Trezor popup denied via Metamask
    if (error.message.includes('Permissions not granted')) return true;
    // 6. Trezor disconnected via Metamask
    if (error.message.includes('device disconnected during action'))
      return true;
    // 7. Fortmatic reject
    if (error.message.includes('Fortmatic: User denied transaction.'))
      return true;
    // 8. Portis Reject
    if (error.message.includes('User denied transaction signature.'))
      return true;
  }
  return false;
};

/*
  Recursive func for calling each transaction in succession after
  the previous one has been signed
*/

export const handleMultipleTransactions = async (
  depositKeys: DepositKeyInterface[],
  provider: unknown,
  account: any,
  updateTransactionStatus: (
    pubkey: string,
    status: TransactionStatus,
    txHash?: string
  ) => void
) => {
  if (!provider) {
    console.error('handleMultipleTransactions: No provider');
    return;
  }

  console.log(
    'handleMultipleTransactions: provider type',
    typeof provider,
    provider
  );

  const web3: any = new Web3(provider as any);
  const contract = new web3.eth.Contract(
    contractAbi,
    CONTRACT_ADDRESS?.toString()
  );

  let gasPrice: string;
  try {
    // Try to get gas price directly from provider to avoid Web3.js issues
    const eip1193Provider = provider as {
      request?: (args: {
        method: string;
        params?: unknown[];
      }) => Promise<unknown>;
    };

    if (eip1193Provider.request) {
      // Use EIP-1193 request directly (no params for eth_gasPrice)
      const gasPriceHex = await eip1193Provider.request({
        method: 'eth_gasPrice',
      });
      gasPrice = gasPriceHex as string;
      console.log('Gas price (direct):', gasPrice);
    } else {
      // Fall back to Web3.js method
      const gasPriceWei = await web3.eth.getGasPrice();
      gasPrice = web3.utils.toHex(gasPriceWei);
      console.log('Gas price (web3):', gasPriceWei, gasPrice);
    }
  } catch (error) {
    console.error('Failed to get gas price:', error);
    // Fall back to a reasonable default gas price (1 gwei for PulseChain)
    gasPrice = web3.utils.toHex('1000000000');
  }

  const transactionParameters = {
    // gasLimit: '0x124f8', TODO set gas limit
    gasPrice,
    from: account as string,
    value: TX_VALUE,
  };

  const remainingTxs = depositKeys.filter(
    key =>
      (key.transactionStatus === TransactionStatus.READY ||
        key.transactionStatus === TransactionStatus.REJECTED) &&
      !inFlightTransactions.has(key.pubkey) // Prevent double deposits (GitLab #5)
  );

  const nextTransaction = remainingTxs.shift();

  if (nextTransaction === undefined) {
    // stop calling yourself
    return;
  }

  const {
    pubkey,
    // eslint-disable-next-line
    withdrawal_credentials,
    signature,
    // eslint-disable-next-line
    deposit_data_root,
  } = nextTransaction;

  // Mark as in-flight to prevent duplicate processing (GitLab #5)
  inFlightTransactions.add(pubkey);

  updateTransactionStatus(pubkey, TransactionStatus.PENDING);

  contract.methods
    .deposit(
      prefix0X(pubkey),
      prefix0X(withdrawal_credentials),
      prefix0X(signature),
      prefix0X(deposit_data_root)
    )
    .send(transactionParameters)
    .on('transactionHash', (txHash: string): void => {
      updateTransactionStatus(pubkey, TransactionStatus.STARTED, txHash);
      handleMultipleTransactions(
        depositKeys,
        provider,
        account,
        updateTransactionStatus
      );
    })
    .on('receipt', () => {
      // do something?
    })
    .on(
      'confirmation',
      (confirmation: number, receipt: { status: {} }): any => {
        console.log('confirmation', confirmation);
        if (confirmation === 0) {
          // Remove from in-flight tracking (GitLab #5)
          inFlightTransactions.delete(pubkey);

          if (receipt.status) {
            updateTransactionStatus(pubkey, TransactionStatus.SUCCEEDED);
          } else {
            updateTransactionStatus(pubkey, TransactionStatus.FAILED);
          }
        }
      }
    )
    .on('error', (error: any) => {
      console.log('error', error);

      // Remove from in-flight tracking (GitLab #5)
      inFlightTransactions.delete(pubkey);

      if (isIgnorableError(error)) return;
      if (isUserRejectionError(error)) {
        updateTransactionStatus(pubkey, TransactionStatus.REJECTED);
      } else {
        updateTransactionStatus(pubkey, TransactionStatus.FAILED);
      }
    });
};
